import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ModalDialog, useWriteLock } from '@kanos/ui';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { LockNote, ProblemAlert } from './card.js';
/** For a dialog that is open while mounted: closing it hands over to the caller. */
export const closedBy = (onClose) => (open) => {
    if (!open)
        onClose();
};
/**
 * Creating something whose secret is shown once (API clients, webhooks): the form dialog, then
 * the secret dialog with what came back.
 */
export function useCreation() {
    const [adding, setAdding] = useState(false);
    const [created, setCreated] = useState(null);
    return {
        adding,
        created,
        start: () => setAdding(true),
        cancel: () => setAdding(false),
        done: (result) => {
            setAdding(false);
            setCreated(result);
        },
        dismiss: () => setCreated(null),
    };
}
/**
 * The one moment a secret is visible (API clients, webhooks): its values to copy, then it is
 * gone - Kanos does not keep it.
 */
export function SecretDialog({ title, description, values, children, onClose, doneLabel = 'Schlüssel ist gesichert', }) {
    return (_jsx(ModalDialog, { title: title, description: description, isOpen: true, onOpenChange: closedBy(onClose), children: (close) => (_jsxs("div", { className: "grid gap-4", children: [_jsx("dl", { className: "grid gap-3", children: values.map((entry) => (_jsx(CopyValue, { ...entry }, entry.label))) }), _jsx("p", { className: "text-sm text-muted", children: children }), _jsx("div", { className: "flex justify-end", children: _jsx(Button, { onPress: close, children: doneLabel }) })] })) }));
}
/** A value to copy, e.g. a key or a DNS record; selectable when the clipboard is refused. */
export function CopyValue({ label, value }) {
    const [copied, setCopied] = useState(false);
    const [failed, setFailed] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setFailed(false);
        }
        catch {
            // The clipboard can be refused (permissions, insecure context): the value stays selectable.
            setFailed(true);
        }
    };
    return (_jsxs("div", { className: "grid gap-1", children: [_jsx("dt", { className: "text-sm font-medium text-muted", children: label }), _jsxs("dd", { className: "flex items-center gap-2 rounded-sm bg-fill px-3 py-2", children: [_jsx("code", { className: "min-w-0 flex-1 break-all font-mono text-sm select-all", children: value }), _jsxs(Button, { variant: "quiet", size: "sm", onPress: () => void copy(), children: [copied ? (_jsx(Check, { "aria-hidden": true, className: "size-4" })) : (_jsx(Copy, { "aria-hidden": true, className: "size-4" })), copied ? 'Kopiert' : 'Kopieren', _jsxs("span", { className: "sr-only", children: [": ", label] })] })] }), failed && (_jsx("p", { role: "alert", className: "text-sm text-danger-fg", children: "Kopieren war nicht m\u00F6glich. Bitte markieren und kopieren Sie den Wert von Hand." }))] }));
}
/**
 * Asks before a weighty step - by default one that cannot be undone; the failure, if any, stays
 * in the dialog.
 */
export function ConfirmDialog({ title, description, confirmLabel, cancelLabel = 'Abbrechen', confirmVariant = 'danger', isPending, error, onConfirm, onClose, }) {
    const lock = useWriteLock();
    return (_jsx(ModalDialog, { title: title, description: description, isOpen: true, onOpenChange: closedBy(onClose), children: (close) => (_jsxs("div", { className: "grid gap-4", children: [lock && _jsx(LockNote, { reason: lock }), error ? _jsx(ProblemAlert, { error: error }) : null, _jsxs("div", { className: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", children: [_jsx(Button, { variant: "quiet", onPress: close, children: cancelLabel }), _jsx(Button, { variant: confirmVariant, isPending: isPending, writes: true, onPress: onConfirm, children: confirmLabel })] })] })) }));
}
//# sourceMappingURL=dialogs.js.map